import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useParams } from 'react-router-dom';
import { Stack, Typography } from '@mui/material';
import { StringParam, useQueryParams } from 'use-query-params';

import { FolderType, useGetOrganizationBySlug } from '@/api/generated';
import DropdownSelect from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import CorePermissionGuard from '@/components/PermissionGuard/CorePermissionGuard';
import { useFolderPermissions } from '@/hooks/permissions/useFolderPermissions';
import { PermissionContextVariables } from '@/hooks/permissions/usePermissions';
import { useSuborganizationPermissions } from '@/hooks/permissions/useSuborganizationPermissions';
import { ReadonlyProvider } from '@/hooks/useReadonly';
import { ORGANIZATION_ITEM_PATH_TYPE } from '@/root/linker';
import typography from '@/theme/typography';
import ClientUserEmailDropdownSelect from '@/views/fields/UserDropdownSelect/ClientUserEmailDropdownSelect';

import { getInvitationLanguageOptions } from './basicOptions';
import FolderAccessSettings from './FolderAccessSettings';

const resolveLocationType = (itemType?: ORGANIZATION_ITEM_PATH_TYPE) => {
  switch (itemType) {
    case ORGANIZATION_ITEM_PATH_TYPE.SUB_ORGANIZATION:
      return FolderType.SUB_ORGANIZATION;
    case ORGANIZATION_ITEM_PATH_TYPE.FOLDER:
      return FolderType.FOLDER;
    default:
      return undefined;
  }
};

const RolesUsersTab: FC = () => {
  const { t } = useTranslation('settings');
  const { t: commonT } = useTranslation('common');
  const match = matchPath(
    { path: '/organizations/:organizationSlug/item-settings/:itemType', end: false },
    location.pathname
  );

  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);
  const itemType = match?.params?.itemType;
  const locationType = useMemo(() => resolveLocationType(itemType as ORGANIZATION_ITEM_PATH_TYPE), [itemType]);
  const permissionVariables = useMemo<PermissionContextVariables | undefined>(
    () => ({
      organizationSlug,
      locationSlug: locationSlug ?? undefined,
      locationType
    }),
    [locationSlug, organizationSlug, locationType]
  );

  const {
    value: {
      canViewRolesAndUsers: canViewRolesAndUsersSuborganization,
      canUpdateRolesAndUsers: canUpdateRolesAndUsersSuborganization
    } = {},
    isLoading: isLoadingSuborganization
  } = useSuborganizationPermissions({
    variables: permissionVariables
  });
  const {
    value: {
      canViewRolesAndUsers: canViewRolesAndUsersFolder,
      canUpdateRolesAndUsers: canUpdateRolesAndUsersFolder
    } = {},
    isLoading: isLoadingFolder
  } = useFolderPermissions({
    variables: permissionVariables
  });

  const canViewRolesAndUsers = useMemo(() => {
    switch (locationType) {
      case FolderType.SUB_ORGANIZATION:
        return canViewRolesAndUsersSuborganization;
      case FolderType.FOLDER:
        return canViewRolesAndUsersFolder;
      default:
        return false;
    }
  }, [canViewRolesAndUsersSuborganization, canViewRolesAndUsersFolder, locationType]);

  const canUpdateRolesAndUsers = useMemo(() => {
    switch (locationType) {
      case FolderType.SUB_ORGANIZATION:
        return canUpdateRolesAndUsersSuborganization;
      case FolderType.FOLDER:
        return canUpdateRolesAndUsersFolder;
      default:
        return false;
    }
  }, [canUpdateRolesAndUsersSuborganization, canUpdateRolesAndUsersFolder, locationType]);

  const adminsLabel = useMemo(
    () =>
      ({
        [FolderType.SUB_ORGANIZATION]: t('orgSettings.fields.subOrganizationAdmins'),
        [FolderType.FOLDER]: t('orgSettings.fields.folderAdmins')
      })[locationType!],
    [locationType, t]
  );

  const isLoading = isLoadingFolder || isLoadingSuborganization;

  const { data: organization } = useGetOrganizationBySlug(
    organizationSlug,
    {},
    { query: { enabled: !!organizationSlug } }
  );

  return (
    <CorePermissionGuard hasPermission={canViewRolesAndUsers} isLoading={isLoading} showAccessDenied>
      <ReadonlyProvider readonly={!canUpdateRolesAndUsers}>
        <Stack
          width="100%"
          sx={{ '& .MuiFormLabel-root': { textAlign: 'start' }, '& .MuiFormControl-root': { pt: 0, px: 0 } }}
        >
          <Stack
            component={Typography}
            direction="row"
            justifyContent="space-between"
            width="100%"
            p={3}
            fontWeight={typography.fontWeightMedium}
          >
            {t('orgSettings.tabs.rolesAndUsers')}
          </Stack>
          <Stack direction="row" mx={3} gap={2}>
            {locationType === FolderType.FOLDER && locationSlug && (
              <FolderAccessSettings organizationSlug={organizationSlug} locationSlug={locationSlug} />
            )}
            {locationType === FolderType.SUB_ORGANIZATION && (
              <>
                <Field
                  component={ClientUserEmailDropdownSelect}
                  name="admins"
                  label={adminsLabel}
                  withFormControl
                  outerSx={{ width: '100%', flex: 1 }}
                  innerProps={{ usePortal: true, organizationId: organization?.id }}
                />
                <Field
                  component={DropdownSelect}
                  name="userInviteEmailLanguage"
                  label={t('orgSettings.fields.invitationLanguage')}
                  innerProps={{
                    options: getInvitationLanguageOptions(commonT),
                    isMulti: false,
                    usePortal: true,
                    sx: {
                      minWidth: 320
                    }
                  }}
                  withFormControl
                />
              </>
            )}
          </Stack>
        </Stack>
      </ReadonlyProvider>
    </CorePermissionGuard>
  );
};

const URL_CONFIG_MAP = {
  locationSlug: StringParam
};

export default RolesUsersTab;
